/**
 * Main content component for the Equipment Status Overview page.
 * This component orchestrates the layout and data flow for the equipment overview.
 */

import React, { useEffect } from 'react';
import { css } from '@emotion/css';
import Alert from '@amzn/meridian/alert';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Divider from "@amzn/meridian/divider";
import Heading from "@amzn/meridian/heading";
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Responsive from '@amzn/meridian/responsive';
import { EquipmentTreePanel } from './tree-panel';
import { KPIPanel, EquipmentHealthSummary } from './kpi-panel';
import { TimelineContainer } from './timeline';
import { TrendChartsContainer } from './trend-charts';
import { ResponsiveLayout } from './responsive-layout';
import { DateRangeSelector, DateRangeLoadingIndicator } from './date-range-selector';
import { GenAISummary } from './genai-summary';
import {
  useEquipmentInitialization,
  useEquipmentSelection,
  useEquipmentData
} from '../../hooks/use-equipment-state';
import { useEquipmentContext } from '../../contexts/equipment-context';
import { useDateRangeWithPersistence } from '../../hooks/use-date-range';
import { useDateRangeCoordination } from '../../hooks/use-date-range-coordination';
import { useTimelineData } from '../../hooks/use-timeline-data';

interface EquipmentStatusContentProps {
  site: string;
  apmId: string;
  selectedNodeId?: string;
}

export const EquipmentStatusContent: React.FC<EquipmentStatusContentProps> = ({
  site,
  apmId,
  selectedNodeId: initialNodeId
}) => {
  // CSS styles for sticky sidebar
  const stickySidebarStyles = css({
    position: 'sticky',
    top: '70px',
    height: 'calc(100vh - 70px)', // Full viewport minus masthead
    overflowY: 'auto', // Enable internal scrolling
    '@media (max-width: 768px)': {
      // Disable sticky on mobile
      position: 'static',
      height: 'auto',
    }
  });

  // Date range state management with URL persistence
  const { dateRange, setDateRange } = useDateRangeWithPersistence('url');
  
  // Coordinate date range changes with data loading
  useDateRangeCoordination();

  // Initialize equipment state
  useEquipmentInitialization(site, apmId, initialNodeId || undefined);

  // Get equipment state from context
  const { selectedNodeId, selectedNode, selectNode, selectNodeById } = useEquipmentSelection();
  const { isLoading, errors, loadEquipmentHierarchy } = useEquipmentData();
  const { state } = useEquipmentContext();

  // Timeline data management for sharing between Timeline and GenAI Summary
  const {
    events: timelineEvents,
    isLoading: timelineLoading,
    error: timelineError,
    hasMore: timelineHasMore,
    loadMore: timelineLoadMore,
    refresh: timelineRefresh,
    clear: timelineClear
  } = useTimelineData({
    site: state.site || site,
    equipmentId: state.apmId || apmId,
    initialLimit: 10,
    autoFetch: true,
    useDateRangeFiltering: true
  });

  // Initialize with URL parameter if provided - only run once on mount
  useEffect(() => {
    if (initialNodeId) {
      selectNodeById(initialNodeId, false); // Add false to disable URL updates during init
    }
  }, [initialNodeId, selectNodeById]); // Fixed: use selectNodeById instead of selectNode

  // Retry handler
  const handleRetry = () => {
    loadEquipmentHierarchy();
  };

  const selectedPositionNodeId = selectedNode?.type === "position" ? selectedNode.id : null;
  const selectedPositionName = selectedNode?.type === "position" ? selectedNode.name : "";

  return (
    <ResponsiveLayout
      isLoading={isLoading.hierarchy}
      error={errors.hierarchy}
      onRetry={handleRetry}
    >
      <Responsive
        query="min-width"
        props={{
          isDesktop: { default: false, '1024px': true },
          isTablet: { default: false, '768px': true },
          isMobile: { default: true, '768px': false },
        }}
      >
        {({ isDesktop, isTablet, isMobile }) => (
          <Row width="100%" spacing={isDesktop ? "600" : "400"} alignmentVertical="top">
            {/* Left panel with equipment tree */}
            <Column
              width={isDesktop ? "320px" : isTablet ? "280px" : "100%"}
              minWidth={isDesktop ? "320px" : isTablet ? "280px" : undefined}
              spacing="400"
              spacingInset="400"
              backgroundColor="highlight"
              height="100%"
              heights="fill"
              className={stickySidebarStyles}
            >
              <EquipmentTreePanel
                site={state.site || site}
                apmId={state.apmId || apmId}
                selectedNodeId={selectedNodeId}
                onNodeSelect={(node) => selectNode(node, true)} // Add true to enable URL updates
              />
            </Column>

            {/* Right panel with content */}
            {(isDesktop || isTablet || !selectedNodeId) && (
              <Column spacing="500" width={isDesktop ? "calc(100% - 340px)" : isTablet ? "calc(100% - 300px)" : "100%"}>
                {/* Equipment Status Overview Section */}
                <Row spacing={isDesktop ? "400" : "300"} alignmentVertical="stretch">
                  {/* Equipment Status Overview Card (Left) */}
                  <Card width={isDesktop ? "50%" : isTablet ? "60%" : "100%"}>
                    <Column spacing="400" spacingInset="400">
                      <Heading level={6} alignment='left'>Equipment Status Overview</Heading>
                      <Row spacing="200" alignmentVertical="center">
                        <Text type="h50">{state.site || site} | {state.apmId || apmId} | {state.position || selectedPositionName}</Text>
                        {state.siteChangeInProgress && (
                          <Row spacing="200" alignmentVertical="center">
                            <Loader size="small" />
                            <Text type="b100" color="secondary">Updating...</Text>
                          </Row>
                        )}
                      </Row>
                      <KPIPanel
                        site={state.site || site}
                        apmId={state.apmId || apmId}
                      />
                    </Column>
                  </Card>

                  {(isDesktop || isTablet) && (
                    <EquipmentHealthSummary
                      site={state.site || site}
                      apmId={state.apmId || apmId}
                    />
                  )}
                </Row>

                {/* Equipment Health Summary Card for Mobile (when stacked) */}
                {!isDesktop && !isTablet && (
                  <EquipmentHealthSummary
                    site={state.site || site}
                    apmId={state.apmId || apmId}
                  />
                )}

              <Divider />

              {/* CBM Analysis Container - Date Range + Signals */}
              <Card>
                <Column spacing="400" spacingInset="400">
                  {/* Date Range Selector */}
                  <Column alignmentHorizontal='end'>
                  <DateRangeSelector
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    onDateRangeChange={setDateRange}
                  />
                  </Column>
                  
                  {/* Trend Charts Component */}
                  <TrendChartsContainer
                    site={state.site || site}
                    equipmentId={state.apmId || apmId || undefined}
                    position={selectedPositionNodeId || undefined}
                    useDateRangeFiltering={true}
                    title="CBM Signals and Models"
                    useGridLayout={true}
                    availableTechnologies={selectedNode?.technologies}
                  />
                </Column>
              </Card>

              {/* Timeline and GenAI Summary - Equal Height Layout */}
              {(isDesktop || isTablet) ? (
                <Row 
                  spacing={isDesktop ? "200" : "150"}
                  alignmentVertical="stretch"
                >
                  <Column heights="fill" width="50%">
                    <TimelineContainer
                      site={state.site || site}
                      equipmentId={state.apmId || apmId || undefined}
                      initialLimit={10}
                      showFilters={true}
                      useDateRangeFiltering={true}
                    />
                  </Column>
                  <Column width="50%" heights="fill">
                    <GenAISummary
                      events={timelineEvents}
                      timelineLoading={timelineLoading}
                    />
                  </Column>
                </Row>
              ) : (
                <Column spacingInset="400" spacing="400">
                  <TimelineContainer
                    site={state.site || site}
                    equipmentId={state.apmId || apmId || undefined}
                    initialLimit={10}
                    showFilters={true}
                    useDateRangeFiltering={true}
                  />
                  <GenAISummary
                    events={timelineEvents}
                    timelineLoading={timelineLoading}
                  />
                </Column>
              )}
            </Column>
            )}
          </Row>
        )}
      </Responsive>
    </ResponsiveLayout>
  );
};
